/**
 * What the onboarding screen says when a customer has no working plan
 * (#2454 Slice 3).
 *
 * **Why the words live here and not on the server.** The server answers WHICH
 * of these applies and nothing else. Sending sentences across the wire would
 * put customer copy in a place where nobody laying out the screen can see it,
 * and would make a wording change a server deploy.
 *
 * **Why they are a table and not five branches in the component.** Because
 * the thing most likely to go wrong is two of them quietly becoming one, and
 * a table can be read top to bottom and checked against the approved mockup
 * in one pass. The mockup is `docs/mockups/2454-billing-gate.html`.
 *
 * **The rules the mockup is binding on**, restated here because this is the
 * file somebody edits:
 *
 *   - Only `none` and `unfinished` mention choosing a plan. The rest are
 *     about somebody who already has one.
 *   - Only `noCredit` offers to take money. Offering it anywhere else sends
 *     somebody to a screen that cannot help them.
 *   - `capped` offers no payment at all. A customer who arrived through our
 *     handoff cannot raise their own limit, because the account service
 *     refuses that on this kind of session, which is what stops a stolen key
 *     lifting the only bound on what it can spend.
 *   - `unknown` says nothing about their plan. During an outage it is what
 *     every paying customer sees, so a word about payment reads as a
 *     cancellation.
 *   - None of them use the words billing, entitlement, account service, or
 *     organization. Every one of those is our vocabulary, not theirs.
 */

/** Which screen to show. Mirrors the server's answer, minus `fine`, which
 * is not a screen: it means let them through. */
export type BillingGateScreen =
  | 'none'
  | 'unfinished'
  | 'no-credit'
  | 'capped'
  | 'blocked'
  | 'unknown'

export interface GateCopy {
  /** The card's heading. */
  title: string
  /** One line under it, framing the situation before the detail. */
  subtitle: string
  /** What is happening, in a sentence. */
  body: string
  /** An optional second line, for the part a customer would otherwise
   * assume wrongly. Used where the obvious next move is the wrong one. */
  aside?: string
  /** The action that actually helps, or none where nothing the customer can
   * do from here would. A button that leads nowhere is worse than no
   * button. */
  action?: { label: string; kind: 'primary' | 'quiet' }
  /** A second action, where looking is useful even though it does not fix
   * anything. */
  secondary?: { label: string; kind: 'quiet' }
}

export const GATE_COPY: Record<BillingGateScreen, GateCopy> = {
  none: {
    title: 'One more step',
    subtitle: 'Choose a plan to start running tests.',
    body: 'Your workspace is ready. Tests need a plan before they can run.',
    action: { label: 'Choose a plan', kind: 'primary' },
  },

  unfinished: {
    // Deliberately not the same words as `none`. Somebody who stopped
    // partway and is shown the first-time screen assumes their attempt
    // vanished, and starts over.
    title: 'Finish choosing your plan',
    subtitle: 'We saved where you left off.',
    body: "Your plan isn't active. Pick up where you left off to start running tests.",
    action: { label: 'Continue', kind: 'primary' },
  },

  'no-credit': {
    title: "You've used this month's allowance",
    subtitle: 'Your plan is active.',
    body: "Tests can't run until you add to your allowance, or until it resets.",
    aside: 'Nothing is wrong with your account and nothing needs setting up.',
    action: { label: 'Add to my allowance', kind: 'primary' },
    secondary: { label: 'See usage', kind: 'quiet' },
  },

  capped: {
    // No payment action anywhere on this one. See the header.
    title: 'Testing is paused by a spending limit',
    subtitle: 'Your plan is active and your balance is fine.',
    body: 'A spending limit on your account has been reached.',
    aside: "Adding money won't change this. If you need the limit raised, talk to us.",
    secondary: { label: 'See usage', kind: 'quiet' },
  },

  blocked: {
    // A refusal we did not recognise. They were stopped and we know it, so
    // blaming our own systems would be as wrong as telling them to pay. Says
    // what is true and sends them to look.
    title: "Tests can't run right now",
    subtitle: 'Something about your plan is stopping them.',
    body: 'Take a look at your plan, and get in touch if it looks fine to you.',
    action: { label: 'Check my plan', kind: 'quiet' },
  },

  unknown: {
    // Not a refusal. We never found out.
    title: "We can't check your plan right now",
    subtitle: 'This is on our side, not yours.',
    body: 'Try again in a minute. If you already have a plan, nothing has changed about it.',
    action: { label: 'Try again', kind: 'primary' },
  },
}

/** Whether a screen is one where offering to take money is correct. Exported
 * so a test can assert the rule rather than restate the table. */
export function offersPayment(screen: BillingGateScreen): boolean {
  const copy = GATE_COPY[screen]
  return copy.action?.label === 'Choose a plan'
    || copy.action?.label === 'Continue'
    || copy.action?.label === 'Add to my allowance'
}
